import { Component, Input, ViewChild, Output, EventEmitter, ElementRef } from '@angular/core';
import { RecipientProvider } from '../../providers/recipient/recipient';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { RouteProvider } from '../../providers/route/route';
import { TranslateService } from '@ngx-translate/core';
import { IRecipient } from '../../model/models/recipient/recipient.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { RmtPopupComponent } from '../rmt-popup/rmt-popup';
import { ScriptProvider } from '../../providers/script/script';
import { PaymentProvider } from '../../providers/payment/payment';
import { CardInfo } from '../../model/models/card/card-info.terface';
import { HttpProvider } from '../../providers/http/http';
import { MobileWalletProvider } from '../../providers/mobile-wallet/mobile-wallet-provider';
import { APP_CONFIG, Env } from '../../environment';
import { OrderProvider } from '../../providers/order/order';
import { ResourceProvider } from '../../providers/resource/resource';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { RecipientMapper } from '../../providers/recipient/recipient.mapper';
import { FormBuilder } from '@angular/forms';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';

declare let createVisaDirectIFrame;
declare let visaDirectTypes;
declare let visaDirectFocus;
declare let getVisaDirectToken;

@Component({
  selector: 'rmt-debit-card',
  templateUrl: 'rmt-debit-card.html'
})
export class RmtDebitCardComponent {
  @Input() recipient: IRecipient;
  @Output() onChange = new EventEmitter<any>();
  @ViewChild('input') input;
  @ViewChild('visaDirectContainer', { read: ElementRef }) visaDirectContainer;
  order: IOrder;
  visaDirectInitialized = false;
  visaDirectBin = null;
  visaDirectValidateFailed = false;
  visaDirectValidateFailedMessage = '';
  visaDirectInvalidNumber = false;
  visaDirectInvalidCountry = false;
  visaDirectInvalidType = false;
  visaDirectInvalidBrand = false;
  visaDirectToken = '';
  hasVisaDirectValue = false;
  hasVisaDirectFocus = false;
  hasVisaDirectLuhnCheck = false;
  providerInfoPromise;
  providerInfo: any = null;
  cardBrand = '';
  country: any = null;
  visaTokenPromise: any;
  visaTokenResolve: any;
  recipientCardNumber = '';
  recipientDebitCardPromise: any = null;
  formGroup;

  constructor(private recipientProvider: RecipientProvider,
              private orderProvider: OrderProvider,
              private scriptProvider: ScriptProvider,
              private paymentProvider: PaymentProvider,
              private mobileWalletProvider: MobileWalletProvider,
              private resourceProvider: ResourceProvider,
              private routeProvider: RouteProvider,
              private formBuilder: FormBuilder,
              private translate: TranslateService) {
    this.order = this.orderProvider.getCurrentOrder();
    this.formGroup = this.formBuilder.group({
      recipientDebitCardNumber: ['']
    });
    this.init();
  }

  ngAfterViewInit() {
    this.initVisaDirect();
  }

  async init() {
    if (this.order.recipient && RecipientMapper.isDirectDebitPayoutAgent(this.order.recipient) && this.order.recipient.bankAccountNumber) {
      let beneficiaryId = this.order.recipient.beneficiaryId;
      // TODO !!!!!!!!!!!!!!!!!!!! temporary hotfix to test it
      if (APP_CONFIG.env !== Env.PROD) {
        if (beneficiaryId === 28683856)
          beneficiaryId = 28308856
        else if (beneficiaryId === 29026756)
          beneficiaryId = 29026356
        if (beneficiaryId !== this.order.recipient.beneficiaryId)
          LoggerProvider.info(this, `Hotfix beneficiaryId changed from ${this.order.recipient.beneficiaryId} to ${beneficiaryId}`);
      }
      this.recipientDebitCardPromise = new Promise(async(resolve) => {
        try {
          var result = await this.recipientProvider.getRecipientDebitCard(beneficiaryId);
          if (result) {
            this.recipientCardNumber = '••••' + this.order.recipient.bankAccountNumber.replace(/\*/g, '').substr(-4);
            this.formGroup.controls.recipientDebitCardNumber.setValue(this.recipientCardNumber);
            this.visaDirectToken = result.cardToken;
            this.cardBrand = result.cardNetworkName;
          }
          resolve(result);
          return;
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Unable to get recipient debit card');
        }
        resolve(null);
      });
    }
    this.providerInfoPromise = this.mobileWalletProvider.getProviderInfo(this.order.countryTo);    
    this.country = await this.resourceProvider.getCountryByValue(this.order.countryTo);
  }

  async save(recipient: IRecipient) {
    if (this.hasVisaDirectFocus) {
      this.hasVisaDirectFocus = false;
      getVisaDirectToken();
    }

    if (this.formGroup.controls.recipientDebitCardNumber.value) {
      if (!this.visaDirectToken) {
        this.formGroup.controls.recipientDebitCardNumber.setValue('');
        this.recipientCardNumber = '';
        RmtPopupComponent.showErrorMessage('Something went wrong - please retry to enter the card number');
        return null;
      }
      LoggerProvider.info(this, 'Got visa direct token from recipient');
    }
    if (this.visaTokenPromise) {
      const resolve = this.visaTokenResolve;
      let timeout = window.setTimeout(() => {
        timeout = null;
        if (resolve)
          resolve(false);
      }, 5000);
      const tokenResult = await this.visaTokenPromise;
      if (timeout)
        window.clearTimeout(timeout);
      if (!tokenResult) {
        RmtPopupComponent.showErrorMessage('Failed to save, please retry to enter the card number');
        return null;
      }
    }

    if (!this.visaDirectToken || !this.order.amountFrom || !this.order.amountTo || !this.order.currencyTo) {
      LoggerProvider.error(this, LoggerEvent.ComponentException, 'Missing required values for Visa Direct');
      return null;
    }

    if (!this.providerInfo) {
      try {
        this.providerInfo = await this.providerInfoPromise;
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Unable to get provider info for Visa Direct');
        return null;
      }
    }
    if (!this.providerInfo) {
      LoggerProvider.error(this, LoggerEvent.ComponentException, 'No provider info for Visa Direct', this.providerInfo);
      this.visaDirectInvalidNumber = true;
      return null;
    }

    const recipientId: any = this.order.recipient.id;
    const amountFrom: any = this.order.amountFrom;
    const amountTo: any = this.order.amountTo;
    let validationResult: any = null;
    try {
      validationResult = await this.mobileWalletProvider.validateMobileWallet(
        amountTo,
        this.visaDirectToken, 
        recipientId, 
        this.providerInfo.CorrespId, 
        this.order.currencyTo,
        this.providerInfo.CorrespLocId,
        [],
        true
      );
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Unable to validate debit card');
      RmtPopupComponent.showException(this, ex, 'Unable to validate debit card', 'Error validating debit card');
      return null;
    }

    this.visaDirectValidateFailedMessage = '';
    if (validationResult && validationResult.RetVal === 100) {
      LoggerProvider.error(this, LoggerEvent.ComponentException, 'Currency does not match', validationResult);
      this.visaDirectValidateFailedMessage = this.translate.instant('form.visaDirect.error.currency', {
        destiantionCurrency: this.order.currencyTo
      });
      this.visaDirectValidateFailed = true;
      return null;
    }
    if (!validationResult || !validationResult.ValidationDetail || !validationResult.ValidationDetail.ValidationToken) {
      LoggerProvider.error(this, LoggerEvent.ComponentException, 'Invalid validation result', validationResult);
      this.visaDirectValidateFailed = true;
      return null;
    }

    this.order.recipient.bankAgentId = this.providerInfo.CorrespId;
    this.order.recipient.bankAgentLocationId = this.providerInfo.CorrespLocId;

    const validationToken = validationResult.ValidationDetail.ValidationToken;
    const validationTokenParts = validationToken.split('|');
    let payload = { 
      ...recipient, 
      bankId: 0, // IMPORTANT: bankId has to be 0
      bankProviderId: this.providerInfo.MapId,
      bankName: `[DebitCard] ${validationTokenParts[1]} ${this.cardBrand}`,
      bankAccountFormatId: null,
      bankAccountNumber: `*********${this.visaDirectToken.substr(-4)}`,
      bankAccountNumberUnitary: '',
    };

    this.order.recipientDebitCard = {
      cardToken: this.visaDirectToken,
      bankAccountNumber: payload.bankAccountNumber,
      cardAuthResponse: validationTokenParts[3],
      cardNetworkId: '122110', // TODO !!!!!!!!!!!!!!!!!!!!! fixed value for now according to Alberto
      cardIssuerId: validationTokenParts[0],
      cardNetworkName: this.cardBrand,
      cardIssuerName: validationTokenParts[1],
      validationToken: validationToken
    };
    this.orderProvider.setCurrentOrder(this.order);

    await this.recipientProvider.saveRecipientBankAccount(payload, this.order.currencyTo, amountFrom, DeliveryMethodType.DebitCard);
    payload = { ...payload, bankAdded: true };

    return payload;
  }

  async initVisaDirect() {
    try {
      const container = this.visaDirectContainer.nativeElement;
      while (container.childNodes.length > 0) {
        container.removeChild(container.childNodes[0]);
      }
      this.visaDirectToken = '';
      if (!this.visaDirectInitialized) {
        LoggerProvider.info(this, 'Initializing Visa Direct');
        await this.scriptProvider.loadVisaDirect();
        this.hasVisaDirectValue = false;
        this.hasVisaDirectLuhnCheck = false;
        this.hasVisaDirectFocus = false;
        createVisaDirectIFrame(
          HttpProvider.authToken, 
          container, 
          async(token) => {
            LoggerProvider.info(this, 'Got Visa Direct Token', token);
            this.visaDirectToken = token;
            if (this.visaTokenPromise && this.visaTokenResolve)
              this.visaTokenResolve(true);
          }, async(ev) => {
            if (ev.type === visaDirectTypes().OnLoaded) {
              LoggerProvider.info(this, 'Loaded Visa Direct');
              if (this.recipientDebitCardPromise) {
                LoggerProvider.info(this, 'Load recipient debit card info');
                await this.recipientDebitCardPromise;
                LoggerProvider.info(this, 'Recipient debit card info loaded');
                this.onChange.emit({ 
                  disabled: false,
                });
              }
              this.visaDirectInitialized = true;
              this.focusVisaDirect()
            } else if (ev.type === visaDirectTypes().OnChange && ev.data) {
              let disabled = true;
              this.visaDirectValidateFailed = false;
              this.hasVisaDirectValue = (ev.data.length > 0);
              this.hasVisaDirectLuhnCheck = false;
              if (ev.data.length === 16) {
                this.hasVisaDirectLuhnCheck = true;
                this.visaDirectInvalidNumber = !ev.data.luhnCheck;
                if (!this.getVisaDirectErrorMessage())
                  disabled = false;
              }
              if (!disabled)
                this.visaTokenPromise = new Promise((resolve) => { this.visaTokenResolve = resolve; });
              this.onChange.emit({ 
                disabled,
              });
            } else if (ev.type === visaDirectTypes().OnFocus) {
              this.visaTokenPromise = null;
              this.hasVisaDirectFocus = true;
              this.formGroup.controls.recipientDebitCardNumber.setValue('')
            } else if (ev.type === visaDirectTypes().OnBlur) {
              if (this.hasVisaDirectFocus) {
                this.hasVisaDirectFocus = false;
                if (this.hasVisaDirectLuhnCheck)
                  getVisaDirectToken();
              }
              if (!this.hasVisaDirectValue && this.recipientDebitCardPromise) {
                this.formGroup.controls.recipientDebitCardNumber.setValue(this.recipientCardNumber);
                this.onChange.emit({ 
                  disabled: false,
                });
              }
            } else if (ev.type === visaDirectTypes().OnBin) {
              if (this.visaDirectBin !== ev.data.bin) {
                if (ev.data.length === 16)
                  this.visaDirectInvalidNumber = !ev.data.luhnCheck;
                this.visaDirectInvalidCountry = false;
              }
              this.visaDirectBin = ev.data.bin;
              try {
                const cardType: CardInfo = await this.paymentProvider.getCardType(ev.data.bin, true);
                if (this.visaDirectBin === ev.data.bin) {
                  this.cardBrand = cardType.cardBrand;
                  this.visaDirectInvalidType = (cardType.paymentMethod !== PaymentMethodType.DebitCard);
                  this.visaDirectInvalidBrand = !this.isVisa();

                  this.visaDirectInvalidNumber = !ev.data.luhnCheck;
                  if (cardType.issuingBankCountryCode !== this.order.countryTo) {
                    LoggerProvider.info(this, `Invalid country ${cardType.issuingBankCountryCode} <=> ${this.order.countryTo}`);
                    this.visaDirectInvalidCountry = true;
                    if (APP_CONFIG.env !== Env.PROD) {
                      const testBins = ['22224000', '41111120', '49884388', '48953702', '48952101', '48952510', '45162000', '40519200', '40518400']
                      if (testBins.includes(ev.data.bin)) {
                        LoggerProvider.info(this, 'Excluded test BIN from country check');
                        this.visaDirectInvalidCountry = false;
                      }
                    }
                  }

                  let disabled = true;
                  if (!this.getVisaDirectErrorMessage())
                    disabled = false;
                  this.onChange.emit({ 
                    disabled,
                  });
                }
              } catch (ex) {
                LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, `Unable to get BIN for ${ev.data.bin}`);
                if (this.visaDirectBin === ev.data.bin) {
                  this.visaDirectInvalidNumber = true;
                }
              }
            }
          },
          { 
            'inline': true,
            'font-size': '16px'
          },
          {
            env: (APP_CONFIG.env === Env.PROD) ? 'prod' : 'stg',
            reportErrors: false,
          }
        );
      }
    } catch (ex) {
      RmtPopupComponent.showException(this, ex, 'Failed to init direct debit number');
    }
  }

  getCountryName() {
    return (this.country) ? this.country.countryName : this.order.countryTo;
  }

  getVisaDirectErrorMessage() {
    if (this.visaDirectInvalidNumber)
      return this.translate.instant('form.visaDirect.error.cardNotValid');
    if (this.visaDirectInvalidCountry)
      return this.translate.instant('form.visaDirect.error.issuerCountry') + ' ' + this.getCountryName();
    if (this.visaDirectInvalidBrand)
      return this.translate.instant('form.visaDirect.error.visaOnly');
    if (this.visaDirectInvalidType)
      return this.translate.instant('form.visaDirect.error.notDebit');
    if (this.visaDirectValidateFailed) {
      if (this.visaDirectValidateFailedMessage)
        return this.visaDirectValidateFailedMessage;
      else
        return this.translate.instant('form.visaDirect.error.genericError');
    }
    if (this.hasVisaDirectValue && !this.hasVisaDirectLuhnCheck && !this.hasVisaDirectFocus)
      return this.translate.instant('form.visaDirect.error.cardNotValid');
    return '';
  }

  focusVisaDirect() {
    visaDirectFocus();
  }

  showVisaDirectInfo() {
    this.routeProvider.openActionSheet(this.routeProvider.pages.DebitCardPayoutInfoPage);
  }

  getNoticeHtml() {
    let translated = this.translate.instant('form.visaDirect.formatDisclaimer', { country: this.getCountryName() });
    translated = translated.replaceAll('\\n', '');
    return translated;
  }

  isVisa() {
    return (this.cardBrand && this.cardBrand.toLowerCase() === 'visa');
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-debit-card/rmt-debit-card.ts